import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { DocumentTypeApi, DocumentTypeOption, documentTypeSchema } from '@/entities/DocumentType';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

type DocumentTypesResponse = {
  items: DocumentTypeApi[];
  total: number;
};

type Params = {
  organizationId: number;
};

type PrivateFields = '_types' | '_selectedKeys';

export class DocumentTypesStore implements ILocalStore {
  static readonly ALL_KEY = '__all__';

  readonly meta = new MetaStore();

  private _types: DocumentTypeOption[] = [];
  private _selectedKeys = new Set<string>();
  private readonly _organizationId: number;

  constructor({ organizationId }: Params) {
    this._organizationId = organizationId;

    makeObservable<DocumentTypesStore, PrivateFields>(this, {
      _types: observable.ref,
      _selectedKeys: observable,
      types: computed,
      selectedIds: computed,
      hasSelection: computed,
      toggle: action.bound,
      reset: action.bound,
    });
  }

  get types(): DocumentTypeOption[] {
    return this._types;
  }

  get selectedIds(): number[] {
    return [...this._selectedKeys].map(Number).filter(Number.isFinite);
  }

  get hasSelection(): boolean {
    return this._selectedKeys.size > 0;
  }

  isSelected(key: string): boolean {
    return this._selectedKeys.has(key);
  }

  toggle(key: string): void {
    if (this._selectedKeys.has(key)) {
      this._selectedKeys.delete(key);
    } else {
      this._selectedKeys.add(key);
    }
  }

  reset(): void {
    this._selectedKeys.clear();
  }

  async load(): Promise<void> {
    if (this.meta.isLoading) {
      return;
    }

    this.meta.setLoadedStartMeta();

    const response = await call<DocumentTypesResponse>({
      endpoint: ENDPOINTS.documents.documentTypes(String(this._organizationId)),
    });

    if (response.isError) {
      this.meta.setLoadedErrorMeta();
      notificationsStore.showError(response.error.response?.data.message ?? 'Не удалось загрузить типы документов');

      return;
    }

    const parsed = schemeParseMultiple(documentTypeSchema, response.data.items);

    runInAction(() => {
      this._types = parsed;
      this.meta.setLoadedSuccessMeta();
    });
  }

  destroy(): void {
    this.meta.destroy();
  }
}
